import { BodyText, Button, Tooltip, toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useAvailableActions } from "@/network/useAvailableActions";
import { useIsMutating } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { OctagonX, Send } from "lucide-react";
import { useCallback, useMemo } from "react";
import { usePharmacyRouteAllPrescriptions } from "../../network/usePharmacyRoutePrescription";
import {
  getPrescriptionRoutabilityBuckets,
  usePrescriptions,
  usePrescriptionsMultipleEncounters,
} from "../../network/usePrescriptions";

type SendAllToPharmacyButtonProps = {
  mode?: "button" | "icon";
} & (
  | {
      encounterId: string;
      childEncounterIds?: never;
    }
  | {
      encounterId?: never;
      childEncounterIds: string[];
    }
);

export const SendAllToPharmacyButton = ({
  encounterId: prescriptionEncounterId,
  childEncounterIds,
  mode = "button",
}: SendAllToPharmacyButtonProps) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: availableActions } = useAvailableActions({
    patientId,
    encounterId: encounterId as string,
  });
  // The provider has taken the visit if any close/complete action is allowed.
  // Each workflow returns a different pair (e.g. CLOSE_ASYNC_RX + REQUIRES_LIVE_VISIT),
  // and these are only enabled after the provider claims the encounter.
  const hasProviderTakenVisit =
    Boolean(availableActions?.COMPLETE?.allowed) ||
    Boolean(availableActions?.CLOSE_ASYNC_RX?.allowed) ||
    Boolean(availableActions?.CLOSE_RX_RENEWAL?.allowed) ||
    Boolean(availableActions?.CLOSE_ASYNC_DX?.allowed) ||
    Boolean(availableActions?.REQUIRES_LIVE_VISIT?.allowed);

  const pharmacyRouteAllPrescriptions = usePharmacyRouteAllPrescriptions({
    patientId: patientId as string,
  });
  const pharmacyRouteAllPrescriptionsCount = useIsMutating({
    mutationKey:
      QueryKeys.Patient.Chart.Prescriptions.ROUTE_PHARMACY_ALL(patientId),
  });
  const isRoutingAllPrescriptions = pharmacyRouteAllPrescriptionsCount > 0;

  const singleEncounterPrescriptions = usePrescriptions({
    patientId: patientId as string,
    encounterId: prescriptionEncounterId,
    enabled: Boolean(prescriptionEncounterId),
  });
  const multipleEncounterPrescriptions = usePrescriptionsMultipleEncounters({
    patientId: patientId as string,
    encounterIds: prescriptionEncounterId ? [] : (childEncounterIds ?? []),
  });
  const singleEncounterPrescriptionBuckets = useMemo(
    () =>
      getPrescriptionRoutabilityBuckets(
        singleEncounterPrescriptions.data?.rxs ?? [],
      ),
    [singleEncounterPrescriptions.data?.rxs],
  );

  const {
    sendable,
    retryable,
    notSendable,
    isPending: isPendingPrescriptions,
    isFetching: isFetchingPrescriptions,
    isError: isErrorPrescriptions,
  } = prescriptionEncounterId
    ? {
        ...singleEncounterPrescriptionBuckets,
        isPending: singleEncounterPrescriptions.isPending,
        isFetching: singleEncounterPrescriptions.isFetching,
        isError: singleEncounterPrescriptions.isError,
      }
    : multipleEncounterPrescriptions;

  const submitAll = useCallback(async () => {
    if (isRoutingAllPrescriptions) {
      return;
    }

    const medicationRequestIds = [
      ...sendable.map((rx) => rx.medicationRequest.id),
      ...retryable.map((rx) => rx.medicationRequest.id),
    ].filter((id): id is string => id !== undefined);

    try {
      const res = await pharmacyRouteAllPrescriptions.mutateAsync({
        medicationRequestIds,
      });

      if (res.data?.failures?.length > 0) {
        toast.error(
          `${res.data.failures.length} of ${medicationRequestIds.length} prescriptions failed to send.`,
          {
            description: "Please try to resend or phone in.",
          },
        );
      }
    } catch {
      toast.error("Failed to submit prescriptions to pharmacy.");
    }
  }, [
    sendable,
    retryable,
    pharmacyRouteAllPrescriptions,
    isRoutingAllPrescriptions,
  ]);

  const total = sendable?.length + retryable?.length + notSendable?.length;
  const totalSendable = sendable?.length + retryable?.length;

  if (mode === "icon" && totalSendable === 0) {
    return null;
  }

  if (mode === "button" && total === 0) {
    return null;
  }

  if (mode === "button" && isPendingPrescriptions) {
    return null;
  }

  const isDisabled =
    !hasProviderTakenVisit ||
    isPendingPrescriptions ||
    isFetchingPrescriptions ||
    isErrorPrescriptions ||
    totalSendable === 0 ||
    isRoutingAllPrescriptions;

  if (mode === "icon") {
    const accessibleLabel = `Send ${totalSendable} Rx's to Pharmacy`;

    return (
      <Tooltip
        tooltipContentText={accessibleLabel}
        side="bottom"
        triggerClassName="flex items-center"
      >
        <Button
          variant="icon"
          className="m-0 p-0 text-link"
          onClick={submitAll}
          isLoading={isRoutingAllPrescriptions}
          disabled={isDisabled}
          aria-label={accessibleLabel}
          data-testid="submit-all-to-pharmacy-icon-button"
        >
          <Send className="h-4 w-4" />
        </Button>
      </Tooltip>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button
        className="w-fit"
        size="xs"
        variant="warning"
        leftIcon={Send}
        onClick={submitAll}
        isLoading={isRoutingAllPrescriptions}
        disabled={isDisabled}
        data-testid="submit-all-to-pharmacy-button"
      >
        Send all Rxs to Pharmacy
      </Button>
      <div className="flex flex-wrap items-center gap-2">
        {totalSendable > 0 && (
          <div className="flex items-center gap-1">
            <BodyText size="xs" textColor="success" bold>
              {`${totalSendable} will be sent`}
            </BodyText>
          </div>
        )}
        {notSendable?.length > 0 && (
          <div className="flex items-center gap-1">
            <OctagonX size={16} />
            <BodyText size="xs" textColor="error" bold>
              {`${notSendable?.length} cannot be sent`}
            </BodyText>
          </div>
        )}
      </div>
    </div>
  );
};
